/**
 * Brand and contact details. Edit these to update across the entire site.
 * TODO: Confirm phone, email, social handles with the business owner.
 */
export const brand = {
  name: 'Uclipse',
  legalEntity: 'Shine and Aroma Home Care Products',
  descriptor: 'Detergents & Chemicals',
  tagline: 'Concentrated cleaning, institutional strength.',
  siteUrl: 'https://www.uclipse.com',
  foundedYear: 2008,
  ogImage: 'https://www.uclipse.com/og-default.jpg',
  description:
    'Uclipse manufactures concentrated cleaning solutions for hotels, hospitals, facilities management, and automotive care. ZED Silver-certified institutional supplier from Gurugram, Haryana.',
  address: {
    line1: 'Khasra No. 436, Gali No. 2',
    line2: 'Village Basai, Railway Colony',
    city: 'Gurugram',
    state: 'Haryana',
    postalCode: '122006',
    country: 'India',
  },
  udyam: 'UDYAM-HR-05-0093200',
  phone: '+91 9311070085',
  email: 'contact@uclipse.com',
  whatsapp: '+91 9311070085', // E.164; wa.me link strips non-digits automatically
  social: {
    instagram: '',
    linkedin: '',
    youtube: '',
  },
} as const;

/**
 * Trust credentials shown across the site (trust bar, footer, share card).
 * Edit here to update everywhere. Every item reflects a real, verifiable claim.
 */
export const credentials = [
  { label: 'ZED Silver Certified', note: 'MSME Sustainable (ZED) Scheme, Govt. of India' },
  { label: 'ISO 9001:2015', note: 'Quality management system' },
  { label: 'Udyam Registered', note: brand.udyam },
  { label: 'Made in India', note: 'Manufactured in Gurugram, Haryana' },
  { label: `Since ${brand.foundedYear}`, note: '15+ years of manufacturing' },
] as const;

export const categories = [
  { slug: 'bathroom', name: 'Bathroom Care' },
  { slug: 'toilet', name: 'Toilet Care' },
  { slug: 'kitchen', name: 'Kitchen Hygiene' },
  { slug: 'dishwash', name: 'Dishwash' },
  { slug: 'floor', name: 'Floor Care' },
  { slug: 'glass', name: 'Glass Care' },
  { slug: 'multipurpose', name: 'Multipurpose' },
  { slug: 'air', name: 'Air Care' },
  { slug: 'hand', name: 'Hand Hygiene' },
  { slug: 'furniture', name: 'Furniture Care' },
  { slug: 'oven', name: 'Oven & Grill' },
  { slug: 'stainless', name: 'Stainless Steel' },
  { slug: 'carpet', name: 'Carpet Care' },
  { slug: 'laundry', name: 'Laundry' },
  { slug: 'kids', name: 'Kid-Safe & Baby' },
  { slug: 'car', name: 'Car Care' },
] as const;

export type CategorySlug = (typeof categories)[number]['slug'];

const categoryNameBySlug: Record<string, string> = Object.fromEntries(
  categories.map((c) => [c.slug, c.name]),
);

/** Human-readable category label for a slug (falls back to the slug itself). */
export function categoryName(slug: string): string {
  return categoryNameBySlug[slug] ?? slug;
}

/**
 * Premium enterprise positioning pillars. Single source of truth for the
 * "Uclipse Standard" — surfaced on the /enterprise hub, the homepage teaser,
 * the header/footer, and each pillar's dedicated page. Edit here to update
 * copy and routing everywhere.
 */
export type PillarId = 'purity' | 'flagship' | 'precision-lab' | 'assurance';

export interface EnterprisePillar {
  id: PillarId;
  /** Icon key resolved by the PillarIcon component. */
  icon: 'purity' | 'flagship' | 'concierge' | 'shield';
  eyebrow: string;
  name: string;
  route: string;
  /** One-line promise used on cards and teasers. */
  blurb: string;
}

export const enterprisePillars: EnterprisePillar[] = [
  {
    id: 'purity',
    icon: 'purity',
    eyebrow: 'Regenerative purity',
    name: 'The Molecular Purity Standard',
    route: '/molecular-purity',
    blurb:
      'Ultra-pure material science and circular-by-design manufacturing — engineered to run cleaner, gentler on assets, and beyond commoditised “eco”.',
  },
  {
    id: 'flagship',
    icon: 'flagship',
    eyebrow: 'The flagship line',
    name: 'Uclipse Bioenzyme',
    route: '/flagship',
    blurb:
      'Our crowning formulation platform — living biological actives that digest soils at the molecular level, batch-traceable and built to the highest tier.',
  },
  {
    id: 'precision-lab',
    icon: 'concierge',
    eyebrow: 'Concierge engineering',
    name: 'The Precision Lab Group',
    route: '/precision-lab',
    blurb:
      'A dedicated engineering partnership: molecular auditing, tailored dilution configurations, and routine optimisation reports for every enterprise account.',
  },
  {
    id: 'assurance',
    icon: 'shield',
    eyebrow: 'Operational assurance',
    name: 'Operational Assurance',
    route: '/assurance',
    blurb:
      'Premium chemistry as insurance against operational failure — protecting uptime, inspections, and worker safety with consistent, compliant supply.',
  },
];

export function pillarByRoute(route: string): EnterprisePillar | undefined {
  return enterprisePillars.find((p) => p.route === route);
}

